import React from "react";
import { CalendarRange, TrendingUp, TrendingDown, ArrowRight, X, Sparkles, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { formatCompact, formatDateID } from "@/lib/currency";

const INS = {
  positive: { Icon: CheckCircle2, cls: "text-emerald-600" },
  warning: { Icon: AlertTriangle, cls: "text-amber-600" },
  info: { Icon: Info, cls: "text-zinc-400" },
};

// Full recap card for the Analisis tab.
export function WeeklyRecapCard({ recap }) {
  if (!recap || !recap.available) {
    return (
      <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="weekly-recap-card">
        <div className="mb-2 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-zinc-400" />
          <p className="font-heading text-sm font-bold text-zinc-900">Ringkasan Mingguan</p>
        </div>
        <p className="text-xs text-zinc-400" data-testid="weekly-recap-empty">
          {recap && recap.noActivity
            ? "Tidak ada aktivitas tercatat minggu lalu. Catatan minggu ini akan diringkas pekan depan."
            : "Ringkasan akan muncul setelah Anda punya satu minggu penuh pencatatan (Senin–Minggu)."}
        </p>
      </div>
    );
  }

  const net = recap.net;
  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="weekly-recap-card">
      <div className="mb-1 flex items-center gap-2">
        <CalendarRange className="h-4 w-4 text-zinc-400" />
        <p className="font-heading text-sm font-bold text-zinc-900">Ringkasan Minggu Lalu</p>
      </div>
      <p className="mb-4 text-xs text-zinc-400">
        {formatDateID(recap.weekStart)} – {formatDateID(recap.weekEnd)}
        {recap.baselineLearning && " · baseline masih dipelajari"}
      </p>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-emerald-50 p-3">
          <div className="flex items-center gap-1 text-emerald-700">
            <TrendingUp className="h-3 w-3" />
            <span className="text-[10px] font-semibold">Masuk</span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold text-emerald-700" data-testid="recap-income">{formatCompact(recap.income)}</p>
        </div>
        <div className="rounded-2xl bg-rose-50 p-3">
          <div className="flex items-center gap-1 text-rose-600">
            <TrendingDown className="h-3 w-3" />
            <span className="text-[10px] font-semibold">Keluar</span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold text-rose-600" data-testid="recap-expense">{formatCompact(recap.expense)}</p>
        </div>
        <div className="rounded-2xl bg-zinc-50 p-3">
          <span className="text-[10px] font-semibold text-zinc-500">{net >= 0 ? "Surplus" : "Defisit"}</span>
          <p className={`mt-1 font-mono text-sm font-bold ${net >= 0 ? "text-zinc-900" : "text-rose-600"}`} data-testid="recap-net">
            {net >= 0 ? "" : "-"}{formatCompact(Math.abs(net))}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2" data-testid="recap-insights">
        {recap.insights.map((ins, i) => {
          const s = INS[ins.level] || INS.info;
          const Icon = s.Icon;
          return (
            <div key={i} className="flex items-start gap-2" data-testid={`recap-insight-${i}`}>
              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${s.cls}`} />
              <p className="text-xs leading-relaxed text-zinc-600">{ins.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Slim, dismissible banner for the Beranda dashboard (start of a new week).
export function WeeklyRecapBanner({ recap, onOpen, onDismiss }) {
  if (!recap || !recap.available) return null;
  const net = recap.net;
  return (
    <div
      className="mt-4 flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-4"
      data-testid="weekly-recap-banner"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
        <Sparkles className="h-5 w-5" />
      </div>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid="recap-banner-open">
        <p className="font-heading text-sm font-bold text-emerald-800">Ringkasan minggu lalu siap</p>
        <p className="mt-0.5 truncate text-xs text-emerald-700">
          Keluar {formatCompact(recap.expense)} · {net >= 0 ? "surplus" : "defisit"} {formatCompact(Math.abs(net))}
        </p>
      </button>
      <ArrowRight className="h-4 w-4 shrink-0 text-emerald-600" />
      <button onClick={onDismiss} className="shrink-0 text-emerald-400 hover:text-emerald-700" data-testid="recap-banner-dismiss" aria-label="Tutup">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
